import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { FormEvent, Fragment, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getCompanyDocumentError } from "../../api/companyDocuments";
import { useCreateCompanyDocumentFolder } from "../../queries/companyDocuments";

interface DocumentFolderDialogProps {
  open: boolean;
  companyId: string;
  parentId: string | null;
  targetBreadcrumb: string[];
  onClose: () => void;
  onCreated: () => void;
}

function validateFolderName(value: string): string | null {
  const name = value.trim();
  if (!name || name.length > 100) {
    return "Folder names must be between 1 and 100 characters.";
  }
  if (name === "." || name === ".." || /[\\/]/.test(name)) {
    return "Folder names cannot be . , .. , /, or \\.";
  }
  return null;
}

export default function DocumentFolderDialog({
  open,
  companyId,
  parentId,
  targetBreadcrumb,
  onClose,
  onCreated,
}: DocumentFolderDialogProps) {
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const createMutation = useCreateCompanyDocumentFolder(companyId);

  useEffect(() => {
    if (open) {
      setName("");
      setError(null);
    }
  }, [open]);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const validationError = validateFolderName(name);
    if (validationError) {
      setError(validationError);
      return;
    }
    try {
      await createMutation.mutateAsync({ parentId, name: name.trim() });
      toast("Folder created.", { type: "success", autoClose: 2000 });
      onCreated();
      onClose();
    } catch (requestError) {
      setError(getCompanyDocumentError(requestError));
    }
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-900/30" />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto p-4">
          <div className="flex min-h-full items-center justify-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-2 sm:scale-95"
            >
              <Dialog.Panel className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
                <div className="flex items-start justify-between">
                  <div>
                    <Dialog.Title className="text-base font-semibold text-gray-900">
                      New folder
                    </Dialog.Title>
                    <p className="mt-1 text-xs text-gray-500">
                      Create inside {targetBreadcrumb.join(" / ")}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                  >
                    <span className="sr-only">Close dialog</span>
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
                <form
                  className="mt-5"
                  onSubmit={(event) => {
                    submit(event).catch(() => undefined);
                  }}
                >
                  <label
                    htmlFor="company-document-folder-name"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Folder name
                  </label>
                  <input
                    id="company-document-folder-name"
                    value={name}
                    autoFocus
                    maxLength={100}
                    onChange={(event) => {
                      setName(event.target.value);
                      if (error) setError(null);
                    }}
                    className="mt-2 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    placeholder="e.g. Legal"
                  />
                  {error && (
                    <p className="mt-2 text-sm text-red-600">{error}</p>
                  )}
                  <div className="mt-5 flex items-center justify-between">
                    <button
                      type="button"
                      onClick={onClose}
                      className="rounded-sm border border-primary px-3 py-2 text-sm font-semibold text-primary hover:bg-primary/10"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={createMutation.isLoading}
                      className="rounded-sm bg-primary px-3 py-2 text-sm font-semibold text-white hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {createMutation.isLoading ? "Creating…" : "Create folder"}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
